"use strict";

import {isset, roundf} from '../js-utilities';
import {
    bmiFromHeightAndWeight,
    cmsToInches,
    generateValueOutput,
    inchesToCms,
    inchesToFeetAndInches,
    parseInput,
    weightFromHeightAndBmi
} from './helpers/calculator-helpers';
import ManagerHelper from './helpers/manager-helper';

/*
 * BMI is more complicated and not as easy to pare down as some of the other controls
 */
export default class BmiManager {
    constructor(controller, language) {
        this.controller = controller;
        this.language = language;
        this.heightSelectUnits = null;

        this.$height = $('#height_select');

        this.$weightSlider = $('#weight_slider');
        this.$weightSliderLowerLimit = $('#weight_slider_lower_limit');
        this.$weightSliderUpperLimit = $('#weight_slider_upper_limit');

        this.$bmiSummary = $('#bmi_summary');
        this.$bmiSummaryValue = $('#bmi_summary_value');

        this.$bmiOutputCellLabel = $('#bmi_output_cell_label');
        this.$bmiOutputCellValue = $('#bmi_output_cell_value');
        // Icon is not being used but since the functionality works I don't want to remove it, in case we use it again
        this.$bmiOutputCellIcon = $('#bmi_output_cell_icon');

        /*
         * Height is a dropdown and not a slider so I don't get to use the manager helper or slider helper here.
         * Maybe I should make the Manager Helper more generic so it works on drop downs too,
         *   but since there are only a few it's probably not worth the time or effort.
         */
        this.$height.on('change', this.updateHeight.bind(this));

        this.weightHelper = new ManagerHelper('weight', this.controller, this.updateBmiAgeInfluence.bind(this));
        this.weightHelper.events.addEventListener('afterUpdate', this.afterUpdateWeight.bind(this));
        this.weightHelper.events.addEventListener('setSliderValueLabel', this.customizeWeightSliderValueLabel.bind(this));

        // this.bmiHelper = new ManagerHelper('bmi', this.controller, this.updateBmiAgeInfluence.bind(this));
        // this.bmiHelper.events.addEventListener('afterUpdate', this.afterUpdateBmi.bind(this));

        this.configureHeightInput();
    }

    reconfigureInputs() {
        if (this.weightHelper) {
            this.weightHelper.configureInput();
        }
        // if (this.bmiHelper) {
        //     this.bmiHelper.configureInput();
        // }
        this.configureHeightInput();
    }

    configureHeightInput() {
        const min = this.controller.constraints.height.min(this.controller.current_values);
        const max = this.controller.constraints.height.max(this.controller.current_values);

        if (this.heightSelectUnits !== this.controller.current_values.imperial) {
            this.$height.empty();

            const blank = new Option('-', '', true);
            blank.disabled = true;
            this.$height.append(blank);

            for (let height = min; height <= max; height++) {
                this.$height.append(new Option(this.controller.current_values.imperial ? inchesToFeetAndInches(height) : height, height, false));
            }
            this.heightSelectUnits = this.controller.current_values.imperial;
        }

        const currentValue = this.$height.val();

        if (isset(this.controller.current_values.height)) {
            const currentHeight = roundf(this.controller.current_values.imperial ? cmsToInches(this.controller.current_values.height) : this.controller.current_values.height, 0);
            this.$height.val(currentHeight);
        } else {
            this.$height.val('');
        }

        const newValue = this.$height.val();

        if (newValue !== currentValue) {
            this.$height.trigger('change');
        }
    }

    afterUpdateWeight(event) {
        const {metricValue} = event.payload;
        const calcValues = this.calculateHeightWeightBmi('weight', {
            height: this.controller.current_values.height,
            weight: metricValue,
            bmi: this.controller.current_values.bmi,
        });
        this.setHeightWeightBmi('weight', calcValues);
    }

    afterUpdateBmi(event) {
        const {metricValue} = event.payload;
        const calcValues = this.calculateHeightWeightBmi('bmi', {
            height: this.controller.current_values.height,
            weight: this.controller.current_values.weight,
            bmi: metricValue,
        });
        this.setHeightWeightBmi('bmi', calcValues);
    }

    updateHeight(event) {
        const inputHeight = parseInput(this.$height.val(), 'float');

        if (event && (!isset(inputHeight) || isNaN(inputHeight))) {
            this.controller.current_values.height = undefined;
            this.updateBmiAgeInfluence();
            return;
        }

        const metricInputHeight = roundf(this.controller.current_values.imperial ? inchesToCms(inputHeight) : inputHeight, 6);
        const newValues = this.calculateHeightWeightBmi('height', {
            height: metricInputHeight,
            weight: this.controller.current_values.weight,
            bmi: this.controller.current_values.bmi,
        });

        if (event && event.type === 'keyup') {
            if (this.controller.validate_input('height', inputHeight, 'height', false)) {
                this.setHeightWeightBmi('height', newValues);
                this.updateBmiAgeInfluence(newValues.bmi);
            }
        } else {
            if (event && this.controller.validate_input('height', inputHeight, 'height', true)) {
                this.setHeightWeightBmi('height', newValues);
                this.updateBmiAgeInfluence();
                this.$height.val(inputHeight);
                // Because Height, BMI, and Weight are inter-dependent and influential, need to reconfigure the inputs when they change
                this.weightHelper.configureInput();
                // this.bmiHelper.configureInput();
            } else if (this.controller.current_values.height) {
                // Separated because imperial and metric might have different rounding precision
                // TODO: This should be updated to use precision specified in constraints
                if (this.controller.current_values.imperial) {
                    const val = roundf(cmsToInches(controller.current_values.height), 6);
                    this.$height.val(val);
                } else {
                    this.$height.val(roundf(this.controller.current_values.height, 6));
                }
            } else {
                this.$height.val('');
            }
        }
        /* The controller's enable controls function will check if all required values are set to enable the calculator.
         * Since height affects which controls are enabled, call it now in case height is set or cleared.
         */
        this.controller.enableControls();
    }

    // Would like to make this a generic method in the manager helper, but there's still enough deviation that this is simpler
    updateBmiAgeInfluence(bmi = this.controller.current_values.bmi) {
        if (isset(bmi) && isset(this.controller.current_values.gender)) {
            // BMI should always be 1 decimal place of precision but I don't want to enforce that
            //   in the controller values and rather only set it when it needs to be constrained.
            const response = this.controller.calculator_4.ageChangeForValue('bmi', roundf(bmi, 6), this.controller.current_values.fractional_age, this.controller.current_values.gender);
            this.populateBmiOutput(response);
        } else {
            this.populateBmiOutput();
        }
    }

    // Would like to make this a generic method in the manager helper, but there's still enough deviation that this is simpler
    populateBmiOutput(response) {
        const bmiOutput = generateValueOutput('bmi', response, this.controller);

        this.controller.current_age_changes.bmi = bmiOutput.ageChange;
        this.controller.current_values.bmi_change = bmiOutput.ageChangeText

        this.$bmiSummary.html(bmiOutput.summary);
        this.$bmiSummaryValue.html(bmiOutput.summaryValue);

        this.$bmiOutputCellLabel.html(bmiOutput.outputCellLabel);
        this.$bmiOutputCellValue.html(bmiOutput.outputCellValue).css('color', bmiOutput.outputCellColor);
        // Icon is not being used but since the functionality works I don't want to remove it, in case we use it again
        this.$bmiOutputCellIcon.prop('src', bmiOutput.outputCellIcon);

        this.controller.calculate_vascular_age();
    }

    customizeWeightSliderValueLabel(event) {
        const {
            sliderValueLabel: weightSliderValueLabel,
            labelPrefix: weightLabelPrefix,
            labelSuffix: weightLabelSuffix,
            emptyLabel: weightEmptyLabel,
            value: weight,
        } = event.payload;

        // If weight is unset, don't customize it and resort to the default "empty label" behavior
        if (weight === undefined) {
            return;
        }

        // Let the dispatchers know we're handling this (customizing it) so it doesn't use the default label behavior
        event.preventDefault();

        const {
            bmi_slider_label_prefix: bmiLabelPrefix,
            bmi_slider_label_suffix: bmiLabelSuffix,
            bmi_slider_label_empty: bmiEmptyLabel,
        } = this.language;

        const {
            precision: bmiPrecision,
            max: bmiMax,
            min: bmiMin,
        } = this.controller.constraints.bmi;

        // Weight has already been "fixed" in the helper that created this event, but BMI needs to be "fixed" for the output
        const bmi = Math.min(bmiMax, Math.max(bmiMin, roundf(this.controller.current_values.bmi, bmiPrecision)));

        weightSliderValueLabel.innerHTML = `${weightLabelPrefix}${weight}${weightLabelSuffix} [${bmiLabelPrefix}${bmi}${bmiLabelSuffix}]`;
    }

    /*
     * BMI, Height, and Weight are inter-dependent and influence one another,
     *   so here we'll calculate the 'other two' values when one of them is changed.
     * The order of the if/assignment statements is important as changing values should have priority over others.
     * This if the 'first' part of the calculate and set pair of functions.
     */
    calculateHeightWeightBmi(valueChanged, {height, weight, bmi}) {
        const newValues = {
            height,
            weight,
            bmi,
        };
        if (valueChanged === 'height' || valueChanged === 'weight') {
            if (newValues.height && newValues.weight) {
                newValues.bmi = bmiFromHeightAndWeight(newValues.height, newValues.weight);
                newValues.bmi = Math.min(newValues.bmi, this.controller.constraints.bmi.max);
                newValues.bmi = Math.max(newValues.bmi, this.controller.constraints.bmi.min);
                newValues.bmi = roundf(newValues.bmi, 6);
            } else {
                newValues.bmi = undefined;
            }
        }
        if (valueChanged === 'height' || valueChanged === 'bmi') {
            if (newValues.height && newValues.bmi) {
                newValues.weight = weightFromHeightAndBmi(newValues.height, newValues.bmi);
                newValues.weight = Math.min(newValues.weight, this.controller.constraints.weight.max(newValues, true));
                newValues.weight = Math.max(newValues.weight, this.controller.constraints.weight.min(newValues, true));
                newValues.weight = roundf(newValues.weight, 6);
            } else {
                newValues.weight = undefined;
            }
        }
        return newValues;
    }

    /*
     * BMI, Height, and Weight are inter-dependent and influence one another,
     *   so here we'll assign all the values that are (presumably) calculated from calculateHeightWeightBmi.
     * The order of the if/assignment statements is important as changing values should have priority over others.
     * This if the 'second' part of the calculate and set pair of functions.
     */
    setHeightWeightBmi(valueChanged, {height, weight, bmi}) {
        height = (typeof height === 'number' && !isNaN(height)) ? roundf(height, 6) : undefined;
        weight = (typeof weight === 'number' && !isNaN(weight)) ? roundf(weight, 6) : undefined;
        bmi = (typeof bmi === 'number' && !isNaN(bmi)) ? roundf(bmi, 6) : undefined;

        // const oldHeight = (typeof this.controller.current_values.height === 'number' && !isNaN(this.controller.current_values.height)) ? roundf(this.controller.current_values.height, 6) : undefined;
        const oldWeight = (typeof this.controller.current_values.weight === 'number' && !isNaN(this.controller.current_values.weight)) ? roundf(this.controller.current_values.weight, 6) : undefined;
        // const oldBmi = (typeof this.controller.current_values.bmi === 'number' && !isNaN(this.controller.current_values.bmi)) ? roundf(this.controller.current_values.bmi, 6) : undefined;

        this.controller.current_values.height = height;
        this.controller.current_values.weight = weight;
        this.controller.current_values.bmi = bmi;

        if (valueChanged === 'height' || valueChanged === 'bmi') {
            if (this.controller.current_values.height && this.controller.current_values.bmi) {
                if (weight !== oldWeight) {
                    this.weightHelper.configureInput();
                }
            }
        }
    }

    // public method for controller to enable and disable UI controls
    enableControls(enable) {
        this.$height.prop('disabled', !enable);
        this.weightHelper.enableControls(this.isHeightSet() && enable);
        // this.bmiHelper.enableControls(this.isHeightSet() && enable);
    }

    isHeightSet() {
        return isset(this.controller.current_values.height) && !isNaN(this.controller.current_values.height);
    }
}
